/**
 * Footer Component
 * Animated footer with correct contact info and proper link routing
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { BRAND } from '../../constants/images';
import { FOOTER_LINKS } from '../../constants/navigation';
import { ScrollReveal } from '../ui/ScrollReveal';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const phoneNumbers = companyInfo.phone.split('/').map((p) => p.trim());

  return (
    <footer className="bg-[#1A1A1A] text-white overflow-hidden" data-testid="footer">
      {/* CTA Section */}
      <ScrollReveal variant="fadeUp" threshold={0.2}>
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 sm:gap-8">
              <div>
                <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mb-2 sm:mb-3">
                  Ready to build the <span className="italic text-[#8B7355]">future</span>?
                </h3>
                <p className="text-white/60 text-base sm:text-lg">Let's discuss your next industrial project.</p>
              </div>
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#8B7355] text-white text-sm sm:text-base font-semibold hover:bg-[#7A6348] transition-all relative overflow-hidden"
                data-testid="footer-cta"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                Get in Touch
                <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform relative z-10" />
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12">
          <ScrollReveal variant="fadeUp" delay={0.1} threshold={0.1}>
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <img
                  src={BRAND.logo}
                  alt={BRAND.logoAlt}
                  className="logo-img h-12 w-auto object-contain"
                />
              </Link>
              <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                Innovators in industrial construction since {companyInfo.foundedYear}. Building excellence with precision and expertise across India.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal variant="fadeUp" delay={0.15} threshold={0.1}>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold tracking-wider uppercase mb-4 sm:mb-6 text-white">Navigation</h4>
              <ul className="space-y-2 sm:space-y-3">
                {FOOTER_LINKS.navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className="text-white/60 hover:text-[#8B7355] text-xs sm:text-sm transition-colors inline-flex items-center gap-1 group"
                    >
                      {item.name}
                      <ArrowUpRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          <ScrollReveal variant="fadeUp" delay={0.2} threshold={0.1}>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold tracking-wider uppercase mb-4 sm:mb-6 text-white">Services</h4>
              <ul className="space-y-2 sm:space-y-3">
                {FOOTER_LINKS.services.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className="text-white/60 hover:text-[#8B7355] text-xs sm:text-sm transition-colors inline-flex items-center gap-1 group"
                    >
                      {item.name}
                      <ArrowUpRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          <ScrollReveal variant="fadeUp" delay={0.25} threshold={0.1}>
            <div>
              <h4 className="text-xs sm:text-sm font-semibold tracking-wider uppercase mb-4 sm:mb-6 text-white">Contact</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-2 sm:gap-3">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#8B7355] mt-0.5 flex-shrink-0" />
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-[#8B7355] text-xs sm:text-sm leading-relaxed transition-colors"
                  >
                    {companyInfo.address}
                  </a>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#8B7355] mt-0.5 flex-shrink-0" />
                  <div>
                    {phoneNumbers.map((num) => (
                      <a
                        key={num}
                        href={`tel:${num.replace(/\s/g, '')}`}
                        className="block text-white/60 hover:text-[#8B7355] text-xs sm:text-sm transition-colors"
                      >
                        {num}
                      </a>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#8B7355] mt-0.5 flex-shrink-0" />
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-white/60 hover:text-[#8B7355] text-xs sm:text-sm transition-colors break-all"
                  >
                    {companyInfo.email}
                  </a>
                </li>
                {companyInfo.businessHours && (
                  <li className="flex items-start gap-2 sm:gap-3">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#8B7355] mt-0.5 flex-shrink-0" />
                    <span className="text-white/60 text-xs sm:text-sm">{companyInfo.businessHours}</span>
                  </li>
                )}
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-3 sm:mt-4 text-[#8B7355] text-xs sm:text-sm font-medium hover:text-[#9A8468] transition-colors"
              >
                Contact us
                <ArrowUpRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-white/40 text-xs sm:text-sm text-center md:text-left">
              © {currentYear} {BRAND.companyName}. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
              {FOOTER_LINKS.legal.map((item) => (
                <a
                  key={item.name}
                  href={item.path}
                  className="text-white/40 hover:text-white/60 text-xs sm:text-sm transition-colors"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
